import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const GtmshTerms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>GTM Super Hero Terms of Service - Tagnetica</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="bingbot" content="noindex, nofollow" />
        <meta name="description" content="GTM Super Hero Terms of Service" />
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <h1 className="text-4xl font-bold text-text-primary mb-6">Terms of Service</h1>
              <p className="text-lg text-text-secondary mb-8">
                <strong>Last updated: 28/06/2025</strong>
              </p>

              <div className="space-y-8 text-text-secondary">
                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">1. Acceptance of Terms</h2>
                  <p>
                    By accessing and using GTM Super Hero ("the Service"), operated by Tagnetica ("we," "our," "us," or "Company"), 
                    you ("User," "you," or "your") accept and agree to be bound by the terms and provision of this agreement. 
                    If you do not agree to abide by the above, please do not use this service.
                  </p>
                  <p className="bg-accent/50 border border-primary/20 rounded-lg p-4 mt-4">
                    <strong>IMPORTANT:</strong> BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS. 
                    IF YOU ARE USING THE SERVICE ON BEHALF OF A COMPANY OR OTHER LEGAL ENTITY, YOU REPRESENT THAT YOU HAVE THE AUTHORITY TO BIND SUCH ENTITY TO THESE TERMS.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">2. Description of Service</h2>
                  <p>
                    GTM Super Hero is a web-based toolkit for analyzing and reviewing Google Tag Manager (GTM) container exports. 
                    The Service provides interactive, visual, and privacy-focused solutions for GTM audits, container exploration, 
                    and quick editing of tag, trigger, and variable names via the GTM API.
                  </p>
                  <p className="bg-accent/50 border border-primary/20 rounded-lg p-4 mt-4">
                    <strong>DISCLAIMER:</strong> THE SERVICE IS PROVIDED FOR INFORMATIONAL AND ANALYTICAL PURPOSES ONLY. 
                    WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR RELIABILITY OF ANY ANALYSIS OR RECOMMENDATIONS PROVIDED THROUGH THE SERVICE.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">3. User Accounts and Registration</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">3.1 Account Creation</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You must create an account to access the Service</li>
                    <li>You must provide accurate, current, and complete information during registration</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You are responsible for all activities that occur under your account</li>
                    <li>You must be at least 18 years old or have parental consent to use the Service</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">3.2 Google OAuth</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The Service uses Google OAuth for authentication</li>
                    <li>By using Google OAuth, you agree to comply with Google's Terms of Service</li>
                    <li>We only access the minimum permissions required to provide the Service</li>
                    <li>We are not responsible for any issues arising from Google OAuth service disruptions</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">4. Service Tiers and Usage Limits</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">4.1 Free Tier</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Limited to 30 total actions (GTM imports + regex generations combined)</li>
                    <li>Full access to all analysis features</li>
                    <li>Google OAuth sign-in required</li>
                    <li>Real-time usage tracking with visual indicators</li>
                    <li><strong>NO WARRANTY OR SUPPORT PROVIDED FOR FREE TIER</strong></li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">4.2 Pro Tier</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Unlimited actions</li>
                    <li>Priority support (best effort only)</li>
                    <li>Advanced analytics</li>
                    <li>Subscription required ($9.99/month)</li>
                    <li><strong>SUPPORT IS PROVIDED ON A BEST-EFFORT BASIS ONLY</strong></li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">5. Acceptable Use</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">5.1 Permitted Uses</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Analyzing your own GTM container exports</li>
                    <li>Generating regex patterns for legitimate business purposes</li>
                    <li>Using the Service for internal business operations</li>
                    <li>Educational and research purposes</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">5.2 Prohibited Uses</h3>
                  <p>You agree not to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Use the Service for any illegal or unauthorized purpose</li>
                    <li>Attempt to gain unauthorized access to the Service or its systems</li>
                    <li>Interfere with or disrupt the Service or servers</li>
                    <li>Use the Service to transmit viruses, malware, or other harmful code</li>
                    <li>Reverse engineer, decompile, or disassemble the Service</li>
                    <li>Use the Service to violate any third-party rights</li>
                    <li>Share account credentials with others</li>
                    <li>Use automated tools to access the Service without permission</li>
                    <li>Use the Service for competitive analysis of our business</li>
                    <li>Attempt to circumvent usage limits or restrictions</li>
                    <li>Use the Service in violation of any applicable laws or regulations</li>
                    <li>Use the Service to store or transmit sensitive personal data</li>
                    <li>Use the Service for any purpose that could damage our reputation</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">6. Data and Privacy</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">6.1 Data Processing</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>GTM container data is processed in your browser and is not stored on our servers</li>
                    <li>We only store usage statistics and account information</li>
                    <li>We do not access or store the content of your GTM containers</li>
                    <li><strong>YOU ARE SOLELY RESPONSIBLE FOR THE CONTENT AND COMPLIANCE OF YOUR GTM CONTAINERS</strong></li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">6.2 Data Compliance</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You are responsible for ensuring your use of the Service complies with applicable data protection laws</li>
                    <li>We are not responsible for any data protection violations resulting from your use of the Service</li>
                    <li>You must obtain necessary consents for any personal data processed through your GTM containers</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">7. Payment and Billing</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">7.1 Pro Tier Subscription</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Pro tier requires a monthly subscription ($9.99/month)</li>
                    <li>Payments are processed through Stripe</li>
                    <li>Subscriptions automatically renew unless cancelled</li>
                    <li><strong>ALL PAYMENTS ARE NON-REFUNDABLE</strong></li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">7.2 Cancellation</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You may cancel your subscription at any time</li>
                    <li>Cancellation takes effect at the end of the current billing period</li>
                    <li>No refunds are provided for partial months or unused services</li>
                    <li>We reserve the right to terminate subscriptions for any reason</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">8. Intellectual Property</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">8.1 Our Rights</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The Service and its content are owned by Tagnetica or our licensors</li>
                    <li>All trademarks, service marks, and trade names are our property</li>
                    <li>You may not use our intellectual property without written permission</li>
                    <li>Our trade secrets and proprietary information are protected</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">8.2 Your Rights</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You retain ownership of your GTM container data</li>
                    <li>You grant us a limited, non-exclusive license to process your data for service provision</li>
                    <li>You represent that you have the right to grant such license</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">9. Disclaimers and Limitations</h2>
                  
                  <h3 className="text-xl font-semibold text-text-primary mb-3">9.1 Service Availability</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The Service is provided "as is" and "as available"</li>
                    <li>We do not guarantee uninterrupted or error-free service</li>
                    <li>We may temporarily suspend the Service for maintenance without notice</li>
                    <li>We are not responsible for any downtime or service interruptions</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-text-primary mb-3 mt-6">9.2 Limitation of Liability</h3>
                  <p className="bg-accent/50 border border-primary/20 rounded-lg p-4">
                    <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL TAGNETICA BE LIABLE FOR:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-4">
                    <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                    <li>ANY LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES</li>
                    <li>ANY DAMAGES RESULTING FROM USE OR INABILITY TO USE THE SERVICE</li>
                    <li>ANY DAMAGES RESULTING FROM DATA LOSS OR CORRUPTION</li>
                    <li>ANY DAMAGES RESULTING FROM THIRD-PARTY SERVICES OR INTEGRATIONS</li>
                  </ul>
                  <p className="mt-4">
                    <strong>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM.</strong>
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-text-primary mb-4">10. Contact Us</h2>
                  <p>
                    If you have any questions about these Terms of Service, please contact us:
                  </p>
                  <div className="bg-accent/30 border border-primary/20 rounded-xl p-6 mt-4">
                    <ul className="list-none space-y-2">
                      <li><strong>Email:</strong> hello@tagnetica.co.uk</li>
                      <li><strong>Website:</strong> https://tagimplementation.tagnetica.co.uk</li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GtmshTerms; 